import { Anchor, Box, Loader, Paper, Stack, Table, Text, Title, useMantineColorScheme } from "@mantine/core";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "../../../../hooks/useTranslation";
import { useFaqMarkdownContent } from "../../hooks/useFaqMarkdownContent";
import { faqMarkdownComponents } from "../../utils/faqMarkdownComponents";
import { useProfileCompletionFaqRulesQuery } from "./hooks/useProfileCompletionFaqTree";

const DAISY_TEAM_EMAIL = "daisy.bugs@soprasteria.com";

// The single, comprehensive /faq/profile-completion page — explanatory copy plus every
// section's every rule (description/tip/reasoning/parameters), all on one page. No
// per-section or per-rule sub-pages — clicking through to browse rules felt like too
// much page-to-page context switching, per direct user feedback.
export const ProfileCompletionFaqOverview: React.FC = () => {
    const { t } = useTranslation();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === "dark";
    const { data, isLoading } = useProfileCompletionFaqRulesQuery();
    const { data: overviewMarkdown, isLoading: isOverviewMarkdownLoading } = useFaqMarkdownContent(
        "/locales/faq/profile-completion-overview"
    );

    return (
        <Paper
            radius="md"
            withBorder={false}
            style={{ overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}
        >
            <Box
                p="md"
                style={{
                    background: "linear-gradient(135deg, var(--mantine-color-brand-8) 0%, var(--mantine-color-brand-6) 100%)",
                }}
            >
                <Text fw={700} size="md" c="white">
                    {t("FAQ_PC_OVERVIEW_TITLE", undefined, "Profile Completion — Understanding Your Score")}
                </Text>
            </Box>

            <Box p="lg" style={{ background: isDark ? "var(--mantine-color-dark-6)" : "white" }}>
                <Stack gap="lg">
                    <Box>
                        {isOverviewMarkdownLoading && <Loader size="sm" />}
                        {!isOverviewMarkdownLoading && overviewMarkdown && (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={faqMarkdownComponents}>
                                {overviewMarkdown}
                            </ReactMarkdown>
                        )}
                    </Box>

                    <Box>
                        <Title order={4} mb={6}>
                            {t("FAQ_PC_CURRENT_RULES_TITLE", undefined, "Current rules curated with HR department")}
                        </Title>
                        {isLoading && <Loader size="sm" />}
                        {!isLoading && data && (
                            <Stack gap="lg" mt={4}>
                                {data.sections.map((section) => (
                                    <Box key={section.sectionId}>
                                        <Text fw={700} size="sm" mb={8}>
                                            {section.sectionName}
                                        </Text>
                                        <Stack gap="sm">
                                            {section.rules.map((rule) => (
                                                <Paper
                                                    key={rule.ruleId}
                                                    p="md"
                                                    withBorder
                                                    style={{
                                                        borderColor: isDark
                                                            ? "var(--mantine-color-dark-4)"
                                                            : "var(--mantine-color-gray-3)",
                                                    }}
                                                >
                                                    <Stack gap="sm">
                                                        <Text fw={600} size="sm">
                                                            {rule.ruleName}
                                                        </Text>

                                                        {rule.description && <Text size="sm">{rule.description}</Text>}

                                                        {rule.tip && (
                                                            <Box>
                                                                <Text
                                                                    size="xs"
                                                                    fw={600}
                                                                    c="dimmed"
                                                                    tt="uppercase"
                                                                    style={{ letterSpacing: "0.06em" }}
                                                                >
                                                                    {t("FAQ_PC_RULE_TIP_LABEL", undefined, "Tip")}
                                                                </Text>
                                                                <Text size="sm">{rule.tip}</Text>
                                                            </Box>
                                                        )}

                                                        {rule.reasoningTemplate && (
                                                            <Box>
                                                                <Text
                                                                    size="xs"
                                                                    fw={600}
                                                                    c="dimmed"
                                                                    tt="uppercase"
                                                                    style={{ letterSpacing: "0.06em" }}
                                                                >
                                                                    {t("FAQ_PC_RULE_REASONING_LABEL", undefined, "Why it matters")}
                                                                </Text>
                                                                <Text size="sm">{rule.reasoningTemplate}</Text>
                                                            </Box>
                                                        )}

                                                        {rule.parameters.length > 0 && (
                                                            <Box>
                                                                <Text
                                                                    size="xs"
                                                                    fw={600}
                                                                    c="dimmed"
                                                                    tt="uppercase"
                                                                    mb={4}
                                                                    style={{ letterSpacing: "0.06em" }}
                                                                >
                                                                    {t("FAQ_PC_RULE_PARAMETERS_LABEL", undefined, "Parameters")}
                                                                </Text>
                                                                <Table withTableBorder withColumnBorders>
                                                                    <Table.Tbody>
                                                                        {rule.parameters.map((parameter) => (
                                                                            <Table.Tr key={parameter.parameterName}>
                                                                                <Table.Td>{parameter.parameterName}</Table.Td>
                                                                                <Table.Td>{parameter.parameterValue}</Table.Td>
                                                                            </Table.Tr>
                                                                        ))}
                                                                    </Table.Tbody>
                                                                </Table>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Text size="sm" c="dimmed">
                        {t("FAQ_PC_CONTACT_INTRO", undefined, "If you need different rules, please contact the Daisy team:")}{" "}
                        <Anchor href={`mailto:${DAISY_TEAM_EMAIL}`}>{DAISY_TEAM_EMAIL}</Anchor>
                    </Text>
                </Stack>
            </Box>
        </Paper>
    );
};

